import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import * as React from 'react';
import { useEffect, useState } from 'react';
import useDebounceFunction from '../../../components/Hooks/useDebouncedFunction';
import teceoSlickerLib, { NavigationDirection } from '../../../components/Slicker/TeceoSlickerLib';
import useZoomGesture from '../../../hooks/useZoomGesture';
import { MediaType } from '../../../types/general.types';

const TIMEOUT = 100;
const FONT_SIZE = 46;
const MARGIN = 24;
const actionsBaseStyle: { position: 'fixed'; fontSize: number } = {
  position: 'fixed',
  fontSize: FONT_SIZE,
};

export interface MediaFromMediaInFullScreenWithZoom {
  url: string;
  type: MediaType;
}

type MediaInFullscreenWithZoomProps = {
  isOpen: boolean;
  onClose: () => void;
  medias?: MediaFromMediaInFullScreenWithZoom[];
  selectedMedia?: MediaFromMediaInFullScreenWithZoom;
};

const Transition = React.forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
);

const MediaInFullscreenWithZoom = ({
  isOpen,
  onClose,
  medias = [],
  selectedMedia,
}: MediaInFullscreenWithZoomProps) => {
  useZoomGesture();

  const hasMoreThanOneMedia = medias.length > 1;
  const activeNavigationButtons = hasMoreThanOneMedia;

  const { handleDebounceFunction: handleDebounceNavigate } = useDebounceFunction({
    debounceTime: 100,
  });

  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (!selectedMedia) {
      return;
    }
    const indexOfSelectedMedia = medias.indexOf(selectedMedia);
    if (indexOfSelectedMedia === -1) {
      return;
    }
    setSelectedIndex(indexOfSelectedMedia);
  }, [medias, selectedMedia]);

  const navigate = (navigateDirection: NavigationDirection) => {
    const nextIndex = teceoSlickerLib.getNextIndex(selectedIndex, navigateDirection, medias.length);
    setSelectedIndex(nextIndex);
  };

  return (
    <Dialog
      fullScreen
      onClose={onClose}
      open={isOpen}
      slots={{
        transition: Transition,
      }}
    >
      {medias.map((media, index) =>
        selectedIndex === index ? (
          <Fade in={selectedIndex === index} timeout={TIMEOUT} key={index}>
            <div>
              {media.type === MediaType.VIDEO ? (
                <video src={media.url} autoPlay controls disablePictureInPicture />
              ) : (
                <img src={media.url} />
              )}
            </div>
          </Fade>
        ) : null,
      )}
      <IconButton
        color="inherit"
        onClick={onClose}
        aria-label="close"
        style={{ ...actionsBaseStyle, top: MARGIN, right: MARGIN }}
        size="large"
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>
      {activeNavigationButtons && (
        <>
          <IconButton
            color="inherit"
            onClick={() => handleDebounceNavigate(() => navigate(-1))}
            aria-label="previous"
            style={{ ...actionsBaseStyle, bottom: '50%', left: MARGIN }}
            size="large"
          >
            <NavigateBeforeIcon fontSize="inherit" />
          </IconButton>

          <IconButton
            color="inherit"
            onClick={() => handleDebounceNavigate(() => navigate(1))}
            aria-label="next"
            style={{ ...actionsBaseStyle, bottom: '50%', right: MARGIN }}
            size="large"
          >
            <NavigateNextIcon fontSize="inherit" />
          </IconButton>
        </>
      )}
    </Dialog>
  );
};

export default MediaInFullscreenWithZoom;
